<template>
  <div>
    <booking-recovery-notice v-if="!needsLogin" :lang="lang" @change="bookingRecovery = $event" />
    <p v-if="loading" class="empty" role="status">{{ $t('intake.portal.loading') }}</p>
    <p v-if="softError" class="empty" role="status">
      {{ needsLogin ? $t('intake.portal.needsLogin') : $t('intake.portal.loadSoftError') }}
      <router-link v-if="needsLogin" class="btn btn-ghost" :to="{name: 'login', params: {lang}}">{{ $t('intake.portal.needsLoginCta') }}</router-link>
    </p>

    <section v-if="!loading && !nextSession && !bookingRecovery.blocksNewBooking" class="client-hero client-hero--empty" aria-labelledby="nextSessionTitle">
      <div class="hero-copy">
        <p class="eyebrow"><svg class="icon icon-inline" aria-hidden="true"><use href="#i-calendar"/></svg> {{ $t('intake.portal.homeNoSessionEyebrow') }}</p>
        <h2 id="nextSessionTitle">{{ emptyHeroTitle }}</h2>
        <p>{{ emptyHeroBody }}</p>
        <div class="client-hero-actions">
          <router-link class="btn btn-primary" :to="emptyHeroCtaTo">{{ emptyHeroCta }}</router-link>
          <router-link class="btn btn-secondary" :to="emptyHeroSecondaryTo">{{ emptyHeroSecondary }}</router-link>
        </div>
      </div>
    </section>

    <section v-else-if="nextSession" class="client-hero" aria-labelledby="nextSessionTitle">
      <div class="hero-date" aria-hidden="true">
        <span class="hero-day">{{ heroDay }}</span>
        <span class="hero-month">{{ heroMonth }}</span>
      </div>
      <div class="hero-copy">
        <p class="eyebrow"><svg class="icon icon-inline" aria-hidden="true"><use href="#i-video"/></svg> {{ $t('intake.portal.homeHeroEyebrow') }}</p>
        <h2 id="nextSessionTitle">{{ heroTitle }}</h2>
        <p>{{ heroBody }}</p>
        <div class="client-hero-actions">
          <router-link class="btn btn-primary" :to="sessionDetailLink(nextSession)">{{ $t('intake.portal.homeHeroCta') }}</router-link>
          <router-link class="btn btn-secondary" :to="r('portalSessions')">{{ $t('intake.portal.homeHeroAlt') }}</router-link>
        </div>
      </div>
    </section>

    <section v-if="!loading" class="summary-grid" :aria-label="$t('intake.portal.homeSummaryAria')">
      <article class="summary-card">
        <span class="ico"><svg class="icon" aria-hidden="true"><use href="#i-check"/></svg></span>
        <h2>{{ $t('intake.portal.homeCardOrsTitle') }}</h2>
        <p>{{ assessmentCardBody }}</p>
        <router-link
          class="btn btn-ghost"
          :to="assessmentCardTo"
        >{{ assessmentCardCta }} <svg class="icon icon-sm" aria-hidden="true"><use href="#i-arrow"/></svg></router-link>
      </article>
      <article class="summary-card">
        <span class="ico"><svg class="icon" aria-hidden="true"><use href="#i-card"/></svg></span>
        <h2>{{ $t('intake.portal.homeCardPayTitle') }}</h2>
        <p>{{ payCardBody }}</p>
        <router-link class="btn btn-ghost" :to="payCardLink">{{ payCardCta }} <svg class="icon icon-sm" aria-hidden="true"><use href="#i-arrow"/></svg></router-link>
      </article>
      <article class="summary-card">
        <span class="ico"><svg class="icon" aria-hidden="true"><use href="#i-support"/></svg></span>
        <h2>{{ $t('intake.portal.homeCardSupportTitle') }}</h2>
        <p>{{ supportCardBody }}</p>
        <router-link class="btn btn-ghost" :to="supportLink">{{ supportCardCta }} <svg class="icon icon-sm" aria-hidden="true"><use href="#i-arrow"/></svg></router-link>
      </article>
    </section>

    <section v-if="!loading" class="client-grid">
      <article class="card">
        <div class="card-head">
          <div class="card-title">
            <span class="card-ico"><svg class="icon" aria-hidden="true"><use href="#i-path"/></svg></span>
            <div><h2>{{ $t('intake.portal.homePathTitle') }}</h2><p>{{ $t('intake.portal.homePathSub') }}</p></div>
          </div>
          <router-link class="btn btn-ghost" :to="r('portalProgress')">{{ $t('intake.portal.homePathCta') }}</router-link>
        </div>
        <div class="journey">
          <div :class="['journey-step', journey1Class]">
            <span class="journey-dot">
              <svg v-if="journey1Done" class="icon icon-sm" aria-hidden="true"><use href="#i-check"/></svg>
              <template v-else>{{ lang === 'fa' ? '۱' : '1' }}</template>
            </span>
            <div><b>{{ $t('intake.portal.homeJ1Title') }}</b><small>{{ journeyStep1Body }}</small></div>
          </div>
          <div :class="['journey-step', journey2Class]">
            <span class="journey-dot">
              <svg v-if="firstSessionCompleted" class="icon icon-sm" aria-hidden="true"><use href="#i-check"/></svg>
              <template v-else>{{ lang === 'fa' ? '۲' : '2' }}</template>
            </span>
            <div><b>{{ $t('intake.portal.homeJ2Title') }}</b><small>{{ journeyStep2Body }}</small></div>
          </div>
          <div :class="['journey-step', journey3Class]">
            <span class="journey-dot">{{ lang === 'fa' ? '۳' : '3' }}</span>
            <div><b>{{ $t('intake.portal.homeJ3Title') }}</b><small>{{ journeyStep3Body }}</small></div>
          </div>
        </div>
      </article>
      <aside class="card therapist-card">
        <div class="who">
          <span class="avatar-lg" aria-hidden="true">{{ therapistAvatar }}</span>
          <span>
            <h2>{{ therapistName }}</h2>
            <p>{{ $t('intake.portal.therapistRole') }}</p>
          </span>
        </div>
        <p>{{ therapistBody }}</p>
        <div class="actions">
          <router-link class="btn btn-primary" :to="r('portalChat')">
            <svg class="icon icon-on-btn" aria-hidden="true"><use href="#i-support"/></svg>
            {{ $t('intake.portal.homeChatCta') }}
          </router-link>
          <router-link class="btn btn-secondary" :to="r('portalRequestNew')">{{ $t('intake.portal.homeRequestCta') }}</router-link>
        </div>
      </aside>
    </section>
  </div>
</template>
<script>
import BookingRecoveryNotice from '@/components/portal/BookingRecoveryNotice.vue'
import {
  buildQualitativePathModel,
  doctorDisplayName,
  doctorInitials,
  factorStateLabel,
  fetchActiveCare,
  fetchAllSessions,
  fetchPendingTreatmentAssessments,
  fetchProfile,
  fetchSupportRequests,
  fetchTreatmentPath,
  fetchUserFactors,
  formatMoneyAmount,
  formatSessionDay,
  formatSessionMeta,
  formatSessionMonth,
  formatSessionTitle,
  getCachedProfile,
  hasAuthToken,
  isPastSession,
  reserveSessionRoute,
  resolveDoctor,
  supportRequestAsListItem,
  supportStatusI18nKey,
  supportTopicI18nKey,
  toFaDigits,
  upcomingFromProfile,
  walletSessionCredit,
} from '@/intake/portalApi'

const CLOSED_SUPPORT = new Set(['CLOSED', 'RESOLVED', 'DONE', 'CANCELLED', 'CANCELED'])

export default {
  name: 'PortalHomeStep',
  components: {BookingRecoveryNotice},
  data() {
    return {
      loading: true,
      softError: false,
      needsLogin: false,
      profile: (typeof getCachedProfile === 'function' ? getCachedProfile() : null),
      pendingAssessments: [],
      factors: [],
      supportItems: [],
      allSessions: [],
      careEpisode: null,
      bookingRecovery: {status: 'LOADING', blocksNewBooking: true},
      pathModel: (typeof buildQualitativePathModel === 'function'
        ? buildQualitativePathModel({})
        : {qualitativePoints: [], gaps: [], rawValuesIncluded: false, points: [], linePoints: ''}),
    }
  },
  computed: {
    lang() {
      return this.$route.params.lang || 'fa'
    },
    upcomingSessions() {
      const fromProfile = typeof upcomingFromProfile === 'function' ? upcomingFromProfile(this.profile) : []
      if (fromProfile.length) return fromProfile
      // Fallback: only date_fixed / doctor_assigned that are not held or cancelled.
      return (this.allSessions || []).filter((s) => {
        if (typeof isPastSession === 'function' && isPastSession(s)) return false
        return true
      })
    },
    nextSession() {
      return this.upcomingSessions[0] || null
    },
    pastSessionCount() {
      const upcomingIds = new Set(this.upcomingSessions.map((s) => s && s.id).filter(Boolean))
      return (this.allSessions || []).filter((s) => {
        const past = typeof isPastSession === 'function' ? isPastSession(s) : false
        return past && !upcomingIds.has(s.id)
      }).length
    },
    doctor() {
      const resolved = typeof resolveDoctor === 'function' ? resolveDoctor(this.profile) : null
      return resolved || (this.nextSession && this.nextSession.doctor) || null
    },
    heroDay() {
      return typeof formatSessionDay === 'function' ? formatSessionDay(this.nextSession, this.lang) : ''
    },
    heroMonth() {
      return typeof formatSessionMonth === 'function' ? formatSessionMonth(this.nextSession, this.lang) : ''
    },
    heroTitle() {
      return typeof formatSessionTitle === 'function' ? formatSessionTitle(this.nextSession, this.lang) : ''
    },
    heroBody() {
      return typeof formatSessionMeta === 'function' ? formatSessionMeta(this.nextSession, this.doctor, this.lang) : ''
    },
    therapistName() {
      const name = typeof doctorDisplayName === 'function' ? doctorDisplayName(this.doctor, this.lang) : ''
      return name || this.$t('intake.portal.therapistNameEmpty')
    },
    therapistAvatar() {
      const value = typeof doctorInitials === 'function' ? doctorInitials(this.doctor, this.lang) : ''
      return value || '—'
    },
    therapistBody() {
      return this.doctor
        ? this.$t('intake.portal.homeTherapistBody')
        : this.$t('intake.portal.homeTherapistBodyEmpty')
    },
    nextPendingAssessment() {
      return (this.pendingAssessments && this.pendingAssessments[0]) || null
    },
    pendingOrs() {
      const next = this.nextPendingAssessment
      if (!next) return null
      const instrument = String(next.instrument || '').toUpperCase()
      return (instrument === 'ORS' || instrument === 'CORS') ? next : null
    },
    pendingSrs() {
      const next = this.nextPendingAssessment
      if (!next) return null
      const instrument = String(next.instrument || '').toUpperCase()
      return (instrument === 'SRS' || instrument === 'CSRS') ? next : null
    },
    assessmentCardBody() {
      if (this.pendingSrs) return this.$t('intake.portal.homeCardSrsBodyPending')
      if (this.nextPendingAssessment) return this.$t('intake.portal.homeCardOrsBodyPending')
      return this.$t('intake.portal.homeCardOrsBodyIdle')
    },
    assessmentCardCta() {
      return this.nextPendingAssessment
        ? this.$t('intake.portal.homeCardOrsCta')
        : this.$t('intake.portal.homeCardOrsCtaIdle')
    },
    assessmentCardTo() {
      const next = this.nextPendingAssessment
      if (!next) return this.r('portalProgress')
      const instrument = String(next.instrument || '').toUpperCase()
      const isSrs = instrument === 'SRS' || instrument === 'CSRS'
      return {
        name: isSrs ? 'intakeOutcomeSrs' : 'intakeOutcomeOrs',
        params: {lang: this.lang},
        query: {sessionId: String(next.sessionId)},
      }
    },
    // Back-compat aliases for journey helpers that still reference these names.
    orsCardBody() {
      return this.assessmentCardBody
    },
    orsLink() {
      return this.assessmentCardTo
    },
    creditCount() {
      return typeof walletSessionCredit === 'function' ? walletSessionCredit(this.profile) : 0
    },
    reserveLink() {
      return typeof reserveSessionRoute === 'function' ? reserveSessionRoute(this.lang) : {name: 'sessions-request', params: {lang: this.lang}}
    },
    needsFirstMatching() {
      return !this.softError && this.creditCount <= 0 && !this.doctor && !this.careEpisode && this.pastSessionCount === 0
    },
    emptyHeroTitle() {
      if (this.needsFirstMatching) return this.$t('intake.portal.homeStartScreeningTitle')
      return this.creditCount > 0
        ? this.$t('intake.portal.homeNoSessionTitleCredit')
        : this.$t('intake.portal.homeNoSessionTitle')
    },
    emptyHeroBody() {
      if (this.needsFirstMatching) return this.$t('intake.portal.homeStartScreeningBody')
      if (this.creditCount > 0) {
        const display = this.lang === 'fa' && typeof toFaDigits === 'function' ? toFaDigits(this.creditCount) : String(this.creditCount)
        return this.$t('intake.portal.homeNoSessionBodyCredit', {n: display})
      }
      return this.$t('intake.portal.homeNoSessionBody')
    },
    emptyHeroCta() {
      if (this.needsFirstMatching) return this.$t('intake.portal.homeStartScreeningCta')
      return this.creditCount > 0
        ? this.$t('intake.portal.homeNoSessionCtaCredit')
        : this.$t('intake.portal.homeNoSessionCta')
    },
    emptyHeroCtaTo() {
      if (this.needsFirstMatching) return this.r('intakeScreening')
      return this.creditCount > 0 ? this.reserveLink : this.r('portalSessions')
    },
    emptyHeroSecondary() {
      if (this.needsFirstMatching) return this.$t('intake.portal.homeNoSessionCta')
      return this.creditCount > 0
        ? this.$t('intake.portal.homeNoSessionCta')
        : this.$t('intake.portal.sessionsBuyCta')
    },
    emptyHeroSecondaryTo() {
      if (this.needsFirstMatching) return this.r('portalSessions')
      return this.creditCount > 0 ? this.r('portalSessions') : this.r('portalPackage')
    },
    payCardCta() {
      if (this.bookingRecovery.blocksNewBooking) return this.lang === 'fa' ? 'بررسی پرداخت و رزرو' : 'Review payment and booking'
      return this.creditCount > 0
        ? this.$t('intake.portal.payCreditCta')
        : this.$t('intake.portal.homeCardPayCta')
    },
    payCardLink() {
      if (this.bookingRecovery.blocksNewBooking) return this.r('portalPayments')
      return this.creditCount > 0 ? this.reserveLink : this.r('portalPayments')
    },
    lastFactor() {
      return this.factors[0] || null
    },
    payCardBody() {
      const n = this.creditCount
      if (n > 0) {
        const display = this.lang === 'fa' && typeof toFaDigits === 'function' ? toFaDigits(n) : String(n)
        return this.$t('intake.portal.homeCardPayBodyCredit', {n: display})
      }
      if (this.lastFactor && typeof factorStateLabel === 'function' && typeof formatMoneyAmount === 'function') {
        const status = factorStateLabel(this.lastFactor, this.lang)
        const amount = formatMoneyAmount(
          this.lastFactor.price,
          this.lastFactor.currency_code || this.lastFactor.currency,
          this.lang,
        )
        return this.$t('intake.portal.homeCardPayBodyFactor', {status, amount})
      }
      return this.$t('intake.portal.homeCardPayBodyIdle')
    },
    openSupportItem() {
      const open = this.supportItems.find((item) => !CLOSED_SUPPORT.has(String(item.status || '').toUpperCase()))
      return open || null
    },
    supportCardBody() {
      if (!this.openSupportItem) return this.$t('intake.portal.homeCardSupportBodyIdle')
      const topicKey = supportTopicI18nKey(this.openSupportItem.topic || this.openSupportItem.title)
      const topicValue = this.$t(topicKey)
      const statusKey = supportStatusI18nKey(this.openSupportItem.status)
      const statusValue = this.$t(statusKey)
      return this.$t('intake.portal.homeCardSupportBodyLive', {
        title: topicValue === topicKey ? this.openSupportItem.title : topicValue,
        status: statusValue === statusKey ? this.openSupportItem.status : statusValue,
      })
    },
    supportCardCta() {
      return this.openSupportItem
        ? this.$t('intake.portal.homeCardSupportCta')
        : this.$t('intake.portal.homeCardSupportCtaIdle')
    },
    supportLink() {
      if (this.openSupportItem && this.openSupportItem.id) {
        return {
          name: 'portalRequestDetail',
          params: {lang: this.lang},
          query: {id: String(this.openSupportItem.id)},
        }
      }
      return this.r('portalSupport')
    },
    journey1Done() {
      return !!(this.doctor || this.careEpisode || this.pastSessionCount > 0 || this.nextSession)
    },
    journey1Class() {
      if (this.journey1Done) return 'done'
      return 'current'
    },
    journeyStep1Body() {
      return this.journey1Done
        ? this.$t('intake.portal.homeJ1Body')
        : this.$t('intake.portal.homeJ1BodyIdle')
    },
    firstSessionCompleted() {
      if (this.careEpisode && this.careEpisode.first_session_state) return this.careEpisode.first_session_state === 'used'
      if (this.careEpisode && Number(this.careEpisode.held_session_count) > 0) return true
      return (this.allSessions || []).some((session) => !session.deleted_at && String(session.state || '').toLowerCase() === 'used')
    },
    journey2Class() {
      if (this.firstSessionCompleted) return 'done'
      if (this.nextSession) return 'current'
      return ''
    },
    journeyStep2Body() {
      if (this.firstSessionCompleted) return this.$t('intake.portal.homeJ2BodyDone')
      if (this.nextSession && typeof formatSessionMeta === 'function') {
        return formatSessionMeta(this.nextSession, this.doctor, this.lang)
      }
      return this.$t('intake.portal.homeJ2BodyIdle')
    },
    pathPointCount() {
      return (this.pathModel && this.pathModel.points && this.pathModel.points.length) || 0
    },
    journey3Class() {
      if (this.nextPendingAssessment) return 'current'
      if (this.pathPointCount > 0) return 'done'
      return ''
    },
    journeyStep3Body() {
      if (this.nextPendingAssessment) return this.$t('intake.portal.homeJ3BodyPending')
      if (this.pathPointCount > 0) {
        const n = this.lang === 'fa' && typeof toFaDigits === 'function'
          ? toFaDigits(this.pathPointCount)
          : String(this.pathPointCount)
        return this.$t('intake.portal.homeJ3BodyLive', {n})
      }
      return this.$t('intake.portal.homeJ3BodyIdle')
    },
  },
  mounted() {
    this.bootstrap()
  },
  methods: {
    r(name) {
      return {name, params: {lang: this.lang}}
    },
    sessionDetailLink(session) {
      return {
        name: 'portalSessionDetail',
        params: {lang: this.lang},
        query: session && session.id ? {id: String(session.id)} : {},
      }
    },
    bootstrap() {
      this.loading = true
      this.softError = false
      this.needsLogin = false
      const hadToken = typeof hasAuthToken === 'function' ? hasAuthToken() : false
      if (!hadToken) {
        this.needsLogin = true
        this.softError = true
        this.loading = false
        return
      }

      const profilePromise = (typeof fetchProfile === 'function' ? fetchProfile() : Promise.resolve(null))
        .then((profile) => {
          this.profile = profile
          return profile
        })
        .catch(() => {
          const cached = typeof getCachedProfile === 'function' ? getCachedProfile() : null
          if (!cached) this.softError = true
          this.profile = cached
          return this.profile
        })

      const pendingPromise = (typeof fetchPendingTreatmentAssessments === 'function'
        ? fetchPendingTreatmentAssessments()
        : Promise.resolve([])).catch(() => [])
      const factorsPromise = (typeof fetchUserFactors === 'function'
        ? fetchUserFactors(1)
        : Promise.resolve([])).catch(() => [])
      const supportPromise = (typeof fetchSupportRequests === 'function'
        ? fetchSupportRequests(1)
        : Promise.resolve({data: []}))
        .then((page) => ((page && page.data) || [])
          .map((r) => (typeof supportRequestAsListItem === 'function' ? supportRequestAsListItem(r, this.lang) : null))
          .filter(Boolean))
        .catch(() => [])
      const carePromise = (typeof fetchActiveCare === 'function' ? fetchActiveCare() : Promise.resolve(null))
        .then((episode) => {
          this.careEpisode = episode || null
          const empty = () => (typeof buildQualitativePathModel === 'function'
            ? buildQualitativePathModel({})
            : {qualitativePoints: [], gaps: [], rawValuesIncluded: false, points: [], linePoints: ''})
          if (!episode || !episode.id || typeof fetchTreatmentPath !== 'function') {
            this.pathModel = empty()
            return null
          }
          return fetchTreatmentPath(episode.id)
            .then((path) => {
              this.pathModel = typeof buildQualitativePathModel === 'function'
                ? buildQualitativePathModel(path || {})
                : empty()
              return path
            })
            .catch(() => {
              this.pathModel = empty()
              return null
            })
        })
        .catch(() => {
          this.careEpisode = null
          this.pathModel = typeof buildQualitativePathModel === 'function'
            ? buildQualitativePathModel({})
            : {qualitativePoints: [], gaps: [], rawValuesIncluded: false, points: [], linePoints: ''}
          return null
        })

      Promise.all([profilePromise, pendingPromise, factorsPromise, supportPromise, carePromise])
        .then(([profile, pending, factors, supportItems]) => {
          this.pendingAssessments = pending || []
          this.factors = factors || []
          this.supportItems = supportItems || []
          const upcoming = typeof upcomingFromProfile === 'function' ? upcomingFromProfile(profile) : []
          if (upcoming.length || typeof fetchAllSessions !== 'function') {
            this.allSessions = []
            return null
          }
          return fetchAllSessions({orderBy: 'session_date:asc'}).catch(() => [])
        })
        .then((sessions) => {
          if (Array.isArray(sessions)) this.allSessions = sessions
        })
        .finally(() => {
          this.loading = false
        })
    },
  },
}
</script>
